import Grid from "@mui/material/Grid";
import {
    Checkbox,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from "@mui/material";
import Button from "@mui/material/Button";
import { Add } from "@mui/icons-material";
import * as React from "react";

export default function PayMethodForm({
    paySystemsData,
    fields,
    mapper,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    project_id,
}) {
    const systems = paySystemsData || [];
    const keyToFieldMapper = mapper?.keyToFieldMapper || {};
    const selectedSystem = systems.find((s) => String(s.id) === String(fields.paysystem_id));
    const isStripe = selectedSystem?.key === 'stripe';
    const fieldLabels = {
        domain: 'Domain',
        apiURL: 'API URL',
        apiKey: 'API Key',
        webhookPublicKey: 'Webhook Public Key',
        brandID: 'Brand ID',
        successRedirect: 'Success Redirect URL',
        failRedirect: 'Fail Redirect URL',
        onsiteS2S: 'Onsite S2S',
        testMode: 'Test mode',
        s2sToken: 'S2S Token',
        skipCapture: 'Skip capture (AVREPAY)',
        apiToken: 'API Token / Username',
        secretKey: 'Secret Key / Password',
        apiBase: 'API Base URL (optional)',
        callbackUrl: 'Callback URL',
        successUrl: 'Success URL',
        failedUrl: 'Fail URL',
        authScheme: 'Auth (hmac / jwt)',
        callbackVerifyMode: 'Callback verify (strict/lenient)',
        createPath: 'Create payment path',
        transferMethod: 'Transfer method',
        bankName: 'Bank name (optional)',
    };

    return (
        <React.Fragment>
            <Grid
                container
                spacing={2}
                component="form"
                onSubmit={handleSubmit}
                autoComplete="off"
            >
                {/* Выбор платёжной системы (Stripe / PayPal / что угодно) */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <InputLabel>Payment System</InputLabel>
                    <Select
                        fullWidth
                        error={!!errors.paysystem_id}
                        required
                        name="paysystem_id"
                        id="paysystem_id"
                        value={fields.paysystem_id || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        readOnly={!!project_id} // нельзя поменять систему для уже привязанного к проекту метода
                    >
                        {systems.map((system) => (
                            <MenuItem key={`id-${system.id}`} value={system.id}>
                                {system.name}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors.paysystem_id && (
                        <FormHelperText>{errors.paysystem_id}</FormHelperText>
                    )}
                </Grid>

                {/* Название метода (как ты его видишь в списке) */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <InputLabel>Name</InputLabel>
                    <TextField
                        error={!!errors.name}
                        value={fields.name || ""}
                        name="name"
                        id="name"
                        required
                        fullWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                    {errors.name && (
                        <FormHelperText>{errors.name}</FormHelperText>
                    )}
                </Grid>

                {/* Stripe grouping tag (used in Atlas → Traffic) */}
                {isStripe && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <InputLabel>Stripe group (Tag)</InputLabel>
                        <TextField
                            error={!!errors.stripe_tag}
                            value={fields.stripe_tag ?? ''}
                            name="stripe_tag"
                            id="stripe_tag"
                            fullWidth
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="e.g. EU, US, VIP, Tag"
                        />
                        {errors.stripe_tag && (
                            <FormHelperText>{errors.stripe_tag}</FormHelperText>
                        )}
                    </Grid>
                )}

                {/* Префикс номера заказа в платеже (KS01, KD001, …) */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <InputLabel>Префикс номера заказа</InputLabel>
                    <TextField
                        error={!!errors.order_id_prefix}
                        value={fields.order_id_prefix ?? ''}
                        name="order_id_prefix"
                        id="order_id_prefix"
                        fullWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="KS, KD, …"
                    />
                    <FormHelperText>Напр. KS → KS01, KS02; KD → KD001</FormHelperText>
                </Grid>
                <Grid size={{ xs: 12, md: 2 }}>
                    <InputLabel>Цифр в номере</InputLabel>
                    <TextField
                        type="number"
                        inputProps={{ min: 1, max: 6 }}
                        error={!!errors.order_id_digits}
                        value={fields.order_id_digits ?? 2}
                        name="order_id_digits"
                        id="order_id_digits"
                        fullWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                </Grid>

                {/* Динамические поля по paymethod_field_mapper
                   Stripe:  publicKey, secretKey, webhookKey, domain
                   PayPal:  clientId, clientSecret, mode, domain
                */}
                {Object.keys(keyToFieldMapper).map((key) => {
                    const fieldName = keyToFieldMapper[key]; // имя поля в БД/форме
                    const value = fields[fieldName] || "";
                    const error = errors[fieldName];
                    const keyLower = String(key).toLowerCase();
                    const prettyLabel = fieldLabels[key] || key;

                    const isSecret =
                        keyLower.includes("secret") ||
                        fieldName.toLowerCase().includes("secret");

                    const isMode =
                        keyLower === "mode" ||
                        fieldName.toLowerCase() === "mode";

                    const isType =
                        keyLower === "type" ||
                        fieldName.toLowerCase() === "type";
                    const isOnsiteField = keyLower === "onsites2s";
                    const isTestModeField = keyLower === "testmode";
                    const isS2STokenField = keyLower === "s2stoken";
                    const isSkipCaptureField = keyLower === "skipcapture";
                    const isTransferMethod = keyLower === "transfermethod";
                    const isAuthScheme = keyLower === "authscheme";
                    const stblOptional = [
                        "bankname", "apibase", "successurl", "failedurl", "authscheme",
                        "callbackverifymode", "callbackurl", "createpath"
                    ];
                    const dynamicRequired = !isOnsiteField && !isSkipCaptureField && !stblOptional.includes(keyLower);

                    return (
                        <Grid key={fieldName} size={{ xs: 12, md: 6 }}>
                            <InputLabel>{prettyLabel}</InputLabel>

                            {/* Для mode (PayPal) делаем выпадающий список */}
                            {isMode ? (
                                <Select
                                    fullWidth
                                    name={fieldName}
                                    id={fieldName}
                                    value={value || "sandbox"}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!error}
                                >
                                    <MenuItem value="sandbox">Sandbox</MenuItem>
                                    <MenuItem value="live">Live</MenuItem>
                                </Select>
                            ) : isType ? (
                                <Select
                                    fullWidth
                                    name={fieldName}
                                    id={fieldName}
                                    value={value || "card"}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!error}
                                >
                                    <MenuItem value="card">card</MenuItem>
                                    <MenuItem value="bank_transfer">bank_transfer</MenuItem>
                                    <MenuItem value="sepa_debit">sepa_debit</MenuItem>
                                    <MenuItem value="ideal">ideal</MenuItem>
                                    <MenuItem value="sofort">sofort</MenuItem>
                                    <MenuItem value="other">other</MenuItem>
                                </Select>
                            ) : isTransferMethod ? (
                                <Select
                                    fullWidth
                                    name={fieldName}
                                    id={fieldName}
                                    value={value || "CARD_NUMBER"}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!error}
                                >
                                    <MenuItem value="CARD_NUMBER">CARD_NUMBER</MenuItem>
                                    <MenuItem value="SBP">SBP</MenuItem>
                                    <MenuItem value="CBU">CBU</MenuItem>
                                    <MenuItem value="ACCOUNT_NUMBER">ACCOUNT_NUMBER</MenuItem>
                                    <MenuItem value="BANK_TRANSFER">BANK_TRANSFER</MenuItem>
                                    <MenuItem value="QR_CODE">QR_CODE</MenuItem>
                                    <MenuItem value="QR_CODE_WITH_ACCOUNT_NUMBER">QR_CODE_WITH_ACCOUNT_NUMBER</MenuItem>
                                    <MenuItem value="JAPAN_ACCOUNT_NUMBER">JAPAN_ACCOUNT_NUMBER</MenuItem>
                                    <MenuItem value="PAGO_MOVIL">PAGO_MOVIL</MenuItem>
                                    <MenuItem value="BOL_ACCOUNT_NUMBER">BOL_ACCOUNT_NUMBER</MenuItem>
                                    <MenuItem value="BOL_QR_CODE">BOL_QR_CODE</MenuItem>
                                    <MenuItem value="VEN_QR_CODE">VEN_QR_CODE</MenuItem>
                                    <MenuItem value="VEN_ACCOUNT_NUMBER">VEN_ACCOUNT_NUMBER</MenuItem>
                                    <MenuItem value="ECU_ACCOUNT_NUMBER">ECU_ACCOUNT_NUMBER</MenuItem>
                                    <MenuItem value="ECU_PHONE_NUMBER">ECU_PHONE_NUMBER</MenuItem>
                                </Select>
                            ) : isAuthScheme ? (
                                <Select
                                    fullWidth
                                    name={fieldName}
                                    id={fieldName}
                                    value={value || "hmac"}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!error}
                                >
                                    <MenuItem value="hmac">HMAC (api_token + api_secret)</MenuItem>
                                    <MenuItem value="jwt">JWT (username + password)</MenuItem>
                                </Select>
                            ) : (
                                <TextField
                                    error={!!error}
                                    value={value}
                                    name={fieldName}
                                    id={fieldName}
                                    required={dynamicRequired}
                                    size="small"
                                    fullWidth
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    type={isSecret ? "password" : "text"}
                                    placeholder={isOnsiteField
                                        ? "empty/false = redirect, true = onsite, or full direct API URL"
                                        : isS2STokenField
                                            ? "Token provided by AVREPAY for direct s2s=true charge"
                                        : ""}
                                />
                            )}

                            {error && (
                                <FormHelperText>{error}</FormHelperText>
                            )}
                            {isOnsiteField && !error && (
                                <FormHelperText>
                                    Включает onsite S2S для этого метода. Значение: true/1/onsite/direct или полный URL direct-charge API.
                                </FormHelperText>
                            )}
                            {isTestModeField && !error && (
                                <FormHelperText>
                                    false для боевого режима, true для sandbox/test.
                                </FormHelperText>
                            )}
                            {isS2STokenField && !error && (
                                <FormHelperText>
                                    Отдельный токен для прямой card-оплаты (не API Key).
                                </FormHelperText>
                            )}
                            {isSkipCaptureField && !error && (
                                <FormHelperText>
                                    Обычно оставьте пустым (capture). true/1/auth = только auth на шлюзе. Если в логах уже есть повтор с skip_capture=true и оба ответа 400 — поле не лечит ошибку, пишите в AVREPAY (лимиты/MID/валюта).
                                </FormHelperText>
                            )}
                            {keyLower === 'domain' && selectedSystem?.key === 'stbl' && !error && (
                                <FormHelperText>
                                    Оплатный домен nginx, как у Stripe: форма карты, /status/success, /status/cancel. Atlas ходит на этот host:20080.
                                </FormHelperText>
                            )}
                            {keyLower === 'apitoken' && selectedSystem?.key === 'stbl' && !error && (
                                <FormHelperText>
                                    HMAC: api_token из кабинета STBL. JWT: username.
                                </FormHelperText>
                            )}
                            {keyLower === 'secretkey' && selectedSystem?.key === 'stbl' && !error && (
                                <FormHelperText>
                                    HMAC: api_secret (показывается один раз). JWT: password.
                                </FormHelperText>
                            )}
                            {keyLower === 'apibase' && !error && (
                                <FormHelperText>
                                    Пусто = через nginx :20080. Для sandbox без прокси: https://sandbox.stbl.team
                                </FormHelperText>
                            )}
                            {keyLower === 'transfermethod' && !error && (
                                <FormHelperText>
                                    Метод STBL H2H. Реквизиты покажем на нашей странице оплаты.
                                </FormHelperText>
                            )}
                            {keyLower === 'authscheme' && !error && (
                                <FormHelperText>
                                    hmac — статическая подпись (как в pay API). jwt — login/password из Authorization.
                                </FormHelperText>
                            )}
                        </Grid>
                    );
                })}

                {/* Лимиты */}
                <Grid size={{ xs: 6, md: 2 }}>
                    <InputLabel>Min Amount</InputLabel>
                    <TextField
                        error={!!errors.min_amount}
                        value={fields.min_amount ?? ""}
                        name="min_amount"
                        id="min_amount"
                        required
                        fullWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                    {errors.min_amount && (
                        <FormHelperText>{errors.min_amount}</FormHelperText>
                    )}
                </Grid>

                <Grid size={{ xs: 6, md: 2 }}>
                    <InputLabel>Max Amount</InputLabel>
                    <TextField
                        error={!!errors.max_amount}
                        value={fields.max_amount ?? ""}
                        name="max_amount"
                        id="max_amount"
                        required
                        fullWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                    {errors.max_amount && (
                        <FormHelperText>{errors.max_amount}</FormHelperText>
                    )}
                </Grid>

                <Grid size={{ xs: 6, md: 2 }}>
                    <InputLabel>Max Day Total</InputLabel>
                    <TextField
                        error={!!errors.max_day_total}
                        value={fields.max_day_total ?? ""}
                        name="max_day_total"
                        id="max_day_total"
                        required
                        fullWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                    {errors.max_day_total && (
                        <FormHelperText>
                            {errors.max_day_total}
                        </FormHelperText>
                    )}
                </Grid>

                <Grid size={{ xs: 6, md: 2 }}>
                    <InputLabel>Max Week Total</InputLabel>
                    <TextField
                        error={!!errors.max_week_total}
                        value={fields.max_week_total ?? ""}
                        name="max_week_total"
                        id="max_week_total"
                        fullWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                    {errors.max_week_total && (
                        <FormHelperText>
                            {errors.max_week_total}
                        </FormHelperText>
                    )}
                </Grid>

                {/* Флаги */}
                <Grid size={{ xs: 3, md: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                name="enabled"
                                checked={
                                    typeof fields.enabled === "boolean"
                                        ? fields.enabled
                                        : true
                                }
                                onChange={handleChange}
                                value={
                                    typeof fields.enabled === "boolean"
                                        ? fields.enabled
                                        : true
                                }
                                color="primary"
                            />
                        }
                        label="Enabled"
                    />
                </Grid>

                <Grid size={{ xs: 3, md: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                name="available"
                                checked={
                                    typeof fields.available === "boolean"
                                        ? fields.available
                                        : true
                                }
                                onChange={handleChange}
                                value={
                                    typeof fields.available === "boolean"
                                        ? fields.available
                                        : true
                                }
                                color="primary"
                            />
                        }
                        label="Available"
                    />
                </Grid>

                <Grid size={{ xs: 3, md: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                name="blocked"
                                checked={!!fields.blocked}
                                onChange={handleChange}
                                color="warning"
                            />
                        }
                        label="Blocked"
                    />
                </Grid>

                {/* Submit */}
                <Grid container spacing={3} sx={{ mt: 2 }} size={12}>
                    <Grid size={{ xs: 6 }}>
                        <Button
                            type="submit"
                            variant="outlined"
                            startIcon={<Add />}
                        >
                            Submit
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </React.Fragment>
    );
}
